import React, { useState, useEffect } from 'react';
import { NamedLink } from '../../components';
import { getAllFortehBlogs, deleteFortehBlog } from '../../util/api';
import { stripHtml } from '../FortehBlogPage/blogHtml';
import css from './FortehBlogSection.module.css';

const FortehBlogSection = ({ isAdmin }) => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('published');
  const [deletingId, setDeletingId] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null); // { id, title }
  const [toast, setToast] = useState(null); // { message, type: 'success' | 'error' }

  const fetchBlogs = () => {
    setLoading(true);
    getAllFortehBlogs({ status: activeTab })
      .then(res => {
        if (res.success) {
          setBlogs(res.data || []);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to fetch blogs:', err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchBlogs();
  }, [activeTab]);

  // Auto-dismiss toast after 4 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const handleDeleteClick = (e, id, title) => {
    e.preventDefault();
    e.stopPropagation();
    setConfirmModal({ id, title });
  };

  const handleConfirmDelete = () => {
    if (!confirmModal) return;
    const { id } = confirmModal;
    setConfirmModal(null);
    setDeletingId(id);

    deleteFortehBlog({ id })
      .then(res => {
        if (res.success) {
          setBlogs(prev => prev.filter(b => b._id !== id));
          showToast(`Blog has been deleted successfully.`, 'success');
        } else {
          showToast('Failed to delete blog: ' + (res.message || 'Unknown error'), 'error');
        }
      })
      .catch(err => {
        console.error('Delete failed:', err);
        showToast('Failed to delete blog. Please try again.', 'error');
      })
      .finally(() => {
        setDeletingId(null);
      });
  };

  return (
    <div className={css.root}>
      <header className={css.header}>
        <h2>Blog</h2>
        {isAdmin && (
          <div className={css.adminControls}>
            <div className={css.tabs}>
              <button
                className={activeTab === 'published' ? css.activeTab : ''}
                onClick={() => setActiveTab('published')}
              >
                Published
              </button>
              <button
                className={activeTab === 'draft' ? css.activeTab : ''}
                onClick={() => setActiveTab('draft')}
              >
                Drafts
              </button>
            </div>
            <NamedLink
              name="BlogTemplateSelector"
              className={css.addBtn}
            >
              + Add New Blog
            </NamedLink>
          </div>
        )}
      </header>

      {loading ? (
        <div className={css.loading}>Loading blogs...</div>
      ) : (
        <div className={css.grid}>
          {blogs?.map((blog, idx) => {
            const data = blog.pageData;
            const slug = blog.slug || '';
            const blogId = blog._id;
            const templateId = blog.templateId || 'original';
            
            // Get title and subtitle based on template
            let blogTitle, blogSubtitle, blogLabel;
            
            if (templateId === 'original') {
              blogTitle = stripHtml(data.hero?.title) || slug;
              blogSubtitle = stripHtml(data.hero?.subtext);
              blogLabel = stripHtml(data.hero?.label) || 'Blog';
            } else {
              blogTitle = stripHtml(data.title) || slug;
              blogSubtitle = stripHtml(data.subtitle || data.intro);
              blogLabel = stripHtml(data.badge) || 'Blog';
            }
            
            const isDeleting = deletingId === blogId;
            return (
              <div key={idx} className={css.cardWrapper}>
                <NamedLink
                  name="FortehBlogPage"
                  params={{ slug }}
                  className={css.card}
                >
                  <div className={css.cardContent}>
                    <span className={css.cardLabel}>{blogLabel}</span>
                    <h3 className={css.cardTitle}>
                      {blogTitle?.length > 60
                        ? `${blogTitle.substring(0, 60)}...`
                        : blogTitle}
                    </h3>
                    <p className={css.cardSub}>{blogSubtitle}</p>
                    <div className={css.cardFooter}>
                      <span className={css.readMore}>Read Article &rarr;</span>
                    </div>
                  </div>
                </NamedLink>

                {isAdmin && (
                  <button
                    className={css.deleteBtn}
                    onClick={(e) => handleDeleteClick(e, blogId, blogTitle)}
                    disabled={isDeleting}
                    title="Delete this blog"
                  >
                    {isDeleting ? (
                      <svg className={css.spinner} viewBox="0 0 24 24" width="18" height="18">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray="31.4 31.4" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        <line x1="10" y1="11" x2="10" y2="17" />
                        <line x1="14" y1="11" x2="14" y2="17" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
            );
          })}
          {blogs?.length === 0 && (
            <div className={css.empty}>No blogs available yet.</div>
          )}
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmModal && (
        <div className={css.modalOverlay} onClick={() => setConfirmModal(null)}>
          <div className={css.modal} onClick={(e) => e.stopPropagation()}>
            <div className={css.modalIcon}>
              <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="#DC3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
            </div>
            <h3 className={css.modalTitle}>Delete Blog</h3>
            <p className={css.modalText}>
              Are you sure you want to delete this blog?
              This action cannot be undone.
            </p>
            <div className={css.modalActions}>
              <button
                className={css.modalCancelBtn}
                onClick={() => setConfirmModal(null)}
              >
                Cancel
              </button>
              <button
                className={css.modalDeleteBtn}
                onClick={handleConfirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className={`${css.toast} ${toast.type === 'error' ? css.toastError : css.toastSuccess}`}>
          <div className={css.toastIcon}>
            {toast.type === 'success' ? (
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            )}
          </div>
          <span className={css.toastMessage}>{toast.message}</span>
          <button className={css.toastClose} onClick={() => setToast(null)}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default FortehBlogSection;
